import { Card, Select, Button, message, Alert, Tag, Space, Switch } from 'antd'
import { DatabaseOutlined, CheckCircleOutlined, SyncOutlined } from '@ant-design/icons'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import axios from 'axios'

function ERPSettings() {
  const { t } = useTranslation()
  const [settings, setSettings] = useState(null)
  const [selectedDb, setSelectedDb] = useState(null)
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [autoSendLoading, setAutoSendLoading] = useState(false)

  const token = localStorage.getItem('token')
  const headers = { Authorization: `Bearer ${token}` }

  useEffect(() => {
    fetchSettings()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchSettings = async () => {
    try {
      const res = await axios.get('/api/settings/erp', { headers })
      setSettings(res.data)
      setSelectedDb(res.data.selected_db)
    } catch (err) {
      message.error(t('erpSettings.fetchError'))
    }
    setLoading(false)
  }

  const saveSelection = async () => {
    if (!selectedDb) {
      message.warning(t('erpSettings.chooseDbWarning'))
      return
    }
    setSaving(true)
    try {
      await axios.put(
        '/api/settings/erp/select-db',
        { selected_db: selectedDb },
        { headers }
      )
      // نحفظ الاختيار في هذا الجهاز تحديداً (localStorage)، ونرسله من
      // الآن فصاعداً مع كل طلب عبر Header — بهذا يبقى هذا الجهاز على
      // اختياره الخاص بمعزل تماماً عن أي جهاز/متصفح آخر لنفس الحساب،
      // حتى لو غيّر جهاز آخر الاختيار العام لاحقاً.
      localStorage.setItem('device_selected_db', selectedDb)
      axios.defaults.headers.common['X-Selected-Db'] = selectedDb
      message.success(t('erpSettings.saveSuccess'))
      fetchSettings()
    } catch (err) {
      message.error(err.response?.data?.error || t('erpSettings.saveError'))
    }
    setSaving(false)
  }

  // يجلب عناوين قواعد البيانات (filecaption) من ERP نفسه ويحفظها،
  // مفيد عند تغيير العميل لاسم/عنوان أي قاعدة على سيرفره لاحقاً
  const refreshCaptions = async () => {
    setRefreshing(true)
    try {
      await axios.post('/api/settings/erp/refresh-captions', {}, { headers })
      message.success(t('erpSettings.refreshSuccess'))
      fetchSettings()
    } catch (err) {
      message.error(err.response?.data?.error || t('erpSettings.refreshError'))
    }
    setRefreshing(false)
  }

  // يعرض العنوان الوصفي فقط إن وُجد (كما يظهر اسم الشركة بدون كود تقني
  // بجانبه) — يُستخدم في تنبيه "قاعدة البيانات الحالية" وفي الهيدر
  const displayLabel = (dbName) => {
    const caption = settings?.database_captions?.[dbName]
    return caption || dbName
  }

  // في قائمة الاختيار نفسها نعرض العنوان + الاسم الخام معاً، لأن
  // المستخدم هنا يحتاج تمييزاً تقنياً واضحاً بين القواعد المتشابهة
  // بالعنوان أو التأكد من القاعدة الصحيحة بالضبط قبل الحفظ
  const optionLabel = (dbName) => {
    const caption = settings?.database_captions?.[dbName]
    return caption ? `${caption} (${dbName})` : dbName
  }

  // تفعيل/تعطيل الإرسال التلقائي — عند التفعيل تفحص الخدمة الخلفية
  // فواتير اليوم كل 30 ثانية (نمطها sendwhatsapp=1 وAutoSendWhatsApp=1)
  const toggleAutoSend = async (checked) => {
    setAutoSendLoading(true)
    try {
      await axios.put(
        '/api/settings/erp/auto-send',
        { enabled: checked },
        { headers }
      )
      message.success(checked ? t('erpSettings.autoSendOnSuccess') : t('erpSettings.autoSendOffSuccess'))
      fetchSettings()
    } catch (err) {
      message.error(err.response?.data?.error || t('erpSettings.autoSendError'))
    }
    setAutoSendLoading(false)
  }

  if (loading) return <div>{t('erpSettings.loadingText')}</div>

  if (!settings || settings.allowed_databases?.length === 0) {
    return (
      <Alert
        type="warning"
        message={t('erpSettings.notConfiguredTitle')}
        description={t('erpSettings.notConfiguredDesc')}
        showIcon
      />
    )
  }

  return (
    <Card title={
      <span>
        <DatabaseOutlined /> {t('erpSettings.cardTitle')}
      </span>
    }>

      {settings.selected_db && (
        <Alert
          type="success"
          message={
            <span>
              {t('erpSettings.currentDbLabel')}
              <Tag color="green" style={{ marginRight: 8 }}>
                {displayLabel(settings.selected_db)}
              </Tag>
            </span>
          }
          icon={<CheckCircleOutlined />}
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <label style={{ fontWeight: 'bold' }}>
            {t('erpSettings.chooseDbLabel')}
          </label>
          <Button
            size="small"
            icon={<SyncOutlined />}
            loading={refreshing}
            onClick={refreshCaptions}
          >
            {t('erpSettings.refreshCaptionsBtn')}
          </Button>
        </div>
        <Select
          style={{ width: '100%' }}
          size="large"
          placeholder={t('erpSettings.choosePlaceholder')}
          value={selectedDb}
          onChange={setSelectedDb}
          options={settings.allowed_databases?.map(db => ({
            label: optionLabel(db),
            value: db
          }))}
        />
      </div>

      <Button
        type="primary"
        size="large"
        block
        loading={saving}
        onClick={saveSelection}
      >
        {t('erpSettings.saveBtn')}
      </Button>

      <div style={{
        marginTop: 24,
        paddingTop: 20,
        borderTop: '1px solid #f0f0f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <div style={{ fontWeight: 'bold' }}>{t('erpSettings.autoSendTitle')}</div>
          <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
            {t('erpSettings.autoSendDesc')}
          </div>
        </div>
        <Switch
          checked={!!settings?.auto_send_enabled}
          loading={autoSendLoading}
          onChange={toggleAutoSend}
        />
      </div>

    </Card>
  )
}

export default ERPSettings
